/**
 * sidebarTools.js — the agent-built tools a person keeps in the Tools rail (2026-10-02).
 *
 * One store for every place that shows or flips it: the rail beside every /tools page
 * (components/AppSidebar.jsx ToolsSidebar), the My tools shelf, a tool's own page and its ACP dock
 * tab. The list lives on the server (GET/POST/DELETE /api/user-tools/sidebar, clarab
 * models/UserToolSidebar.js), so it follows the account rather than the browser; until someone
 * changes it, it is every tool they built.
 *
 * Module-level, so the rail does not refetch on every tool navigation. The last answer is also kept
 * in localStorage per login, so after a reload the section paints at once (the official tools below
 * it do not jump), then revalidates. Reloads: first use, older than a minute (mount, window focus),
 * and whenever a tool is created, versioned, deleted or re-statused in this tab (USER_TOOLS_CHANGED
 * from api.js). Add / remove are optimistic and roll back with the server's message.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { toolsApi, errorText, USER_TOOLS_CHANGED } from './api';

const STALE_MS = 60 * 1000;
const cacheKey = (uid) => `clara.tools.rail.v1:${uid}`;

/** The fields the rail needs from any tool object (a full /api/user-tools view or a rail entry). */
export const slimTool = (t) => ({ id: t.id, name: t.name, description: t.description || '', status: t.status, mine: !!t.mine, owner: { name: t.owner?.name || '' } });
export const withTool = (data, tool) => (data.tools.some((t) => t.id === tool.id) ? data : { ...data, tools: [...data.tools, slimTool(tool)] });
export const withoutTool = (data, id) => ({ ...data, tools: data.tools.filter((t) => t.id !== id) });

const EMPTY = { tools: [], available: [], custom: false, max: 12 };

let state = { userId: null, data: null, loadedAt: 0, error: null };
let inflight = null;
let loads = 0;  // every load started — only the newest one's answer is kept
let writes = 0; // bumped by every add/remove — a load that started before one is stale on arrival
const listeners = new Set();
const set = (patch) => { state = { ...state, ...patch }; listeners.forEach((l) => l()); };

function readCache(uid) {
  try {
    const raw = JSON.parse(localStorage.getItem(cacheKey(uid)) || 'null');
    return raw && Array.isArray(raw.tools) && Array.isArray(raw.available) ? raw : null;
  } catch { return null; }
}
function writeCache(uid, data) {
  try { localStorage.setItem(cacheKey(uid), JSON.stringify(data)); } catch { /* private mode / quota */ }
}

function bindUser(uid) {
  if (state.userId === uid) return;
  inflight = null;
  set({ userId: uid, data: uid ? readCache(uid) : null, loadedAt: 0, error: null });
}

// `force` (a tool was just saved, deleted, re-statused) never rides on a load already in flight —
// that one may have left before the change.
export function loadSidebarTools({ force = false } = {}) {
  const uid = state.userId;
  if (!uid) return Promise.resolve(null);
  if (inflight && !force) return inflight;
  if (!force && state.loadedAt && Date.now() - state.loadedAt < STALE_MS) return Promise.resolve(state.data);
  const seq = ++loads;
  const w = writes;
  const p = toolsApi.sidebar()
    .then((data) => {
      // a newer load, an add/remove or another login since this one left → its answer is stale
      if (state.userId !== uid || seq !== loads || w !== writes) return state.data;
      writeCache(uid, data);
      set({ data, loadedAt: Date.now(), error: null });
      return data;
    })
    .catch((e) => { if (state.userId === uid && seq === loads) set({ error: errorText(e, 'Could not load your tools') }); return null; })
    .finally(() => { if (inflight === p) inflight = null; });
  inflight = p;
  return p;
}

async function write(call, optimistic) {
  const uid = state.userId;
  const before = state.data;
  writes += 1;
  set({ data: optimistic(before || EMPTY), error: null });
  try {
    const data = await call();
    if (state.userId === uid) { writeCache(uid, data); set({ data, loadedAt: Date.now() }); }
    return data;
  } catch (e) {
    if (state.userId === uid) set({ data: before });
    throw new Error(errorText(e, 'Could not update the sidebar'));
  }
}
export const addToSidebar = (tool) => write(() => toolsApi.sidebarAdd(tool.id), (d) => withTool(d, tool));
export const removeFromSidebar = (id) => write(() => toolsApi.sidebarRemove(id), (d) => withoutTool(d, id));

// Reloads happen only while something on screen shows the list (`active` consumers): the quick search
// is mounted on every page but only enabled on /tools. With nobody showing it, a changed tool just
// marks the list stale so the next mount fetches it.
let active = 0;
let wired = false;
function wire() {
  if (wired || typeof window === 'undefined') return;
  wired = true;
  window.addEventListener(USER_TOOLS_CHANGED, () => {
    if (active > 0) loadSidebarTools({ force: true });
    else state = { ...state, loadedAt: 0 };
  });
  window.addEventListener('focus', () => { if (active > 0) loadSidebarTools(); });
}
const subscribe = (l) => { listeners.add(l); wire(); return () => listeners.delete(l); };
const snapshot = () => state;

/**
 * The rail list for the signed-in person (`userId` = their user id; null = nobody signed in).
 *   { ready, tools, available, max, error, has(id), add(tool), remove(id) }
 * `tools` is the rail in order; `available` every tool that could go there (own first, then team).
 * `enabled: false` = mounted but not showing it (loads nothing, answers empty).
 */
export function useSidebarTools(userId, { enabled = true } = {}) {
  const uid = userId ? String(userId) : null;
  const s = useSyncExternalStore(subscribe, snapshot);
  useEffect(() => {
    if (!enabled) return undefined;
    active += 1;
    bindUser(uid);
    loadSidebarTools();
    return () => { active -= 1; };
  }, [uid, enabled]);
  const data = enabled && s.userId === uid ? s.data : null;
  return {
    ready: !!data,
    tools: data?.tools || [],
    available: data?.available || [],
    max: data?.max || EMPTY.max,
    error: enabled && s.userId === uid ? s.error : null,
    has: (id) => !!data?.tools.some((t) => t.id === id),
    add: addToSidebar,
    remove: removeFromSidebar,
  };
}

/** For tests. */
export function _resetSidebarTools() { state = { userId: null, data: null, loadedAt: 0, error: null }; inflight = null; loads = 0; writes = 0; active = 0; }
